import {DatepickerDateRules} from '../datepicker-date-rules';
import {DatepickerDayContext, DatepickerDateFilter, DatepickerDateMode, DatepickerMonthContext} from '../datepicker.models';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  isSignal,
  model,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChildren,
} from '@angular/core';
import {DOCUMENT, NgTemplateOutlet} from '@angular/common';
import {LocalDate} from '@js-joda/core';
import {AurCalendarComponent} from '../calendar/aur-calendar.component';
import {AnchorMonthPosition, DatepickerValue, DatepickerViewMode, DatepickerSelectionMode, DatepickerStartView} from '../datepicker.models';
import {AUR_CALENDAR_LOCALE} from '../calendar/calendar-locale';
import {CalendarPeriodCell} from '../calendar/calendar-period.models';
import {DatepickerNavigation} from './datepicker-navigation';
import {DateRangeSelection} from './date-range-selection';
import {browserCalendarToday} from './browser-calendar-today';

/** Выбор даты или диапазона на одном либо двух месяцах. Форма и API остаются у потребителя. */
@Component({
  selector: 'aur-datepicker',
  imports: [AurCalendarComponent, NgTemplateOutlet],
  templateUrl: './aur-datepicker.component.html',
  styleUrl: './aur-datepicker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.compact-on-small-screens]': 'compactOnSmallScreens()',
    '[class.mobile-layout]': 'mobile()',
  },
})
export class AurDatepickerComponent {
  /** Шаблон под числом дня; данные и поиск по дате принадлежат родителю. */
  public readonly dayContent = input<TemplateRef<DatepickerDayContext> | null>(null);
  /** Без значения — сегодня в зоне браузера; LocalDate переопределяет день, null скрывает отметку. */
  public readonly today = input<LocalDate | null | undefined>(undefined);
  private readonly browserToday = browserCalendarToday();
  protected readonly displayedToday = computed(() => {
    const override = this.today();
    return override === undefined ? this.browserToday() : override;
  });
  /** Блокировка стрелок сетки дней оболочкой; экраны месяцев/лет используют minDate/maxDate. */
  public readonly previousDisabled = input(false);
  public readonly nextDisabled = input(false);

  /** Встроенную навигацию можно скрыть, если кнопками управляет внешняя оболочка. */
  public readonly showNavigation = input(true);
  /** Полные видимые месяцы, включая первую отрисовку; не является событием выбора. */
  public readonly visibleRangeChange = output<DatepickerValue>();
  private lastVisibleRange = '';

  /** Дополняет сетку до шести недель пустыми ячейками, без соседних дат. */
  public readonly fixedSixWeeks = input(false);
  /** На узком экране сохраняет базовую ширину месяца вместо растягивания. */
  public readonly compactOnSmallScreens = input(false);
  /** annual зацикливает месяцы текущего года; в double/range сохраняется обычный режим. */
  public readonly dateMode = input<DatepickerDateMode>('date');
  private readonly currentYear = LocalDate.now().year();
  protected readonly annualYear = computed(() =>
    this.dateMode() === 'annual' && this.viewMode() === 'single' && this.selectionMode() === 'single'
      ? this.currentYear : null
  );

  /** Начальный экран дней, месяцев или лет для одного и двух календарей. */
  public readonly startView = input<DatepickerStartView>('day');
  /** Один либо два месяца; на viewport до 600px отображается один, даже для диапазона. */
  public readonly viewMode = input<DatepickerViewMode>('single');
  /** Одиночный выбор завершается одним кликом, диапазон — двумя. */
  public readonly selectionMode = input<DatepickerSelectionMode>('single');
  /** В range каждый клик выбирает конец относительно этого дня; null сохраняет выбор двумя кликами. */
  public readonly fixedStartDate = input<LocalDate | null>(null);
  /** Готовый выбор хранится в модели и выдаётся через selectedChange. Черновик хранится отдельно. */
  public readonly selected = model<DatepickerValue | null>(null);
  /** Дата первого клика нового диапазона: для подписи черновика и блокировки применения. */
  public readonly selectionStarted = output<LocalDate>();
  /** Содержимое под календарями. В double сохраняет left/right даже при одном месяце на мобильном. */
  public readonly monthFooter = input<TemplateRef<DatepickerMonthContext> | null>(null);
  /** Опорный месяц при открытии; по умолчанию — текущий в системной зоне. */
  public readonly anchorMonth = input(LocalDate.now());
  /** Слева: опорный + следующий. Справа: предыдущий + опорный. */
  public readonly anchorPosition = input<AnchorMonthPosition>('left');

  /** Минимальная дата включительно; null не ограничивает начало. */
  public readonly minDate = input<LocalDate | null>(null);
  /** Максимальная дата включительно; null не ограничивает конец. */
  public readonly maxDate = input<LocalDate | null>(null);
  /** Дополнительное правило доступности дня; не запрещает день внутри диапазона. */
  public readonly dateFilter = input<DatepickerDateFilter | null>(null);
  protected readonly effectiveMinDate = computed(() => {
    const fixed = this.selectionMode() === 'range' ? this.fixedStartDate() : null;
    const min = this.minDate();
    return fixed && (!min || fixed.isAfter(min)) ? fixed : min;
  });

  protected readonly firstMonth = signal(LocalDate.now().withDayOfMonth(1));
  protected readonly secondMonth = computed(() => this.firstMonth().plusMonths(1));
  private readonly anchorDate = signal<LocalDate | null>(null);
  private readonly previewDate = signal<LocalDate | null>(null);
  protected readonly highlight = computed(() => {
    const first = this.anchorDate();
    const selected = this.selected();
    if (this.selectionMode() === 'single') {
      if (!selected) {
        return null;
      }
      const year = this.annualYear();
      const date = year === null ? selected.start : selected.start.withYear(year);
      // 29 февраля невисокосного года не превращаем в выбранное 28 февраля.
      return date.dayOfMonth() === selected.start.dayOfMonth() ? DateRangeSelection.between(date, date) : null;
    }
    const fixed = this.fixedStartDate();
    if (fixed) {
      const end = selected?.end ?? fixed;
      return end.isBefore(fixed) ? {start: fixed, end: fixed} : {start: fixed, end};
    }
    return first ? DateRangeSelection.between(first, this.previewDate() ?? first) : selected;
  });

  private readonly viewport = inject(DOCUMENT).defaultView?.matchMedia('(max-width: 600px)');
  /** null сохраняет автоматический режим; оболочка может явно задать мобильную компоновку. */
  public readonly mobileLayout = input<boolean | null>(null);
  private readonly viewportMobile = signal(this.viewport?.matches ?? false);
  protected readonly mobile = computed(() => this.mobileLayout() ?? this.viewportMobile());
  protected readonly monthCount = computed(() => this.viewMode() === 'double' && !this.mobile() ? 2 : 1);
  protected readonly visibleMonths = computed(() => this.monthCount() === 2
    ? [this.firstMonth(), this.secondMonth()] : [this.firstMonth()]);
  protected readonly activePane = signal(0);
  private periodsOpenedFromDays = false;
  protected readonly headerMonths = computed(() => {
    if (this.activeView() === 'day') {
      return this.visibleMonths();
    }
    const first = this.activeDate().withDayOfMonth(1).minusMonths(this.activePane());
    return this.monthCount() === 2 ? [first, first.plusMonths(1)] : [first];
  });
  protected readonly activeDate = signal(LocalDate.now().withDayOfMonth(1));
  protected readonly focusDate = signal<LocalDate | null>(null);
  private readonly calendars = viewChildren(AurCalendarComponent);
  private readonly localeSource = inject(AUR_CALENDAR_LOCALE);
  protected get locale() {
    return isSignal(this.localeSource) ? this.localeSource() : this.localeSource;
  }
  protected readonly activeView = signal<DatepickerStartView>('day');
  protected readonly periodFocus = signal(0);
  protected readonly yearStart = computed(() => DatepickerNavigation.yearPage(
    this.activeDate().year(), this.anchorMonth().year(), this.effectiveMinDate(), this.maxDate(),
  ));
  protected readonly periodColumns = computed(() => this.mobile() ? 3 : 4);
  protected readonly periodRows = computed(() => {
    const isMonth = this.activeView() === 'month';
    const count = 12;
    const columns = this.periodColumns();
    const chosen = this.highlight()?.start;
    const cells: CalendarPeriodCell[] = Array.from({length: count}, (_, index) => {
      const value = isMonth ? index + 1 : this.yearStart() + index;
      const start = LocalDate.of(isMonth ? this.activeDate().year() : value, isMonth ? value : 1, 1);
      const end = isMonth ? start.plusMonths(1).minusDays(1) : start.plusYears(1).minusDays(1);
      return {
        value,
        label: isMonth ? this.locale.months[index] : String(value),
        disabled: !DatepickerDateRules.hasSelectableDay(
          start, end, this.effectiveMinDate(), this.maxDate(), this.dateFilter(),
        ),
        selected: !!chosen && chosen.year() === start.year() && (!isMonth || chosen.monthValue() === value),
      };
    });
    return Array.from({length: count / columns}, (_, row) => cells.slice(row * columns, (row + 1) * columns));
  });
  protected readonly previousLabel = computed(() => this.activeView() === 'day' ? this.locale.previousMonth
    : this.activeView() === 'month' ? this.locale.previousYear : this.locale.previousYears);
  protected readonly nextLabel = computed(() => this.activeView() === 'day' ? this.locale.nextMonth
    : this.activeView() === 'month' ? this.locale.nextYear : this.locale.nextYears);
  private readonly periodButtons = viewChildren<ElementRef<HTMLButtonElement>>('periodButton');
  private readonly pendingPeriodFocus = signal<number | null>(null);

  protected readonly periodLabel = computed(() => this.annualYear() !== null
    ? this.monthLabel(this.activeDate())
    : this.activeView() === 'month' ? String(this.activeDate().year())
      : this.yearStart() + '–' + (this.yearStart() + 11));

  protected readonly previousBlocked = computed(() => !this.canNavigate(-1));
  protected readonly nextBlocked = computed(() => !this.canNavigate(1));

  constructor() {
    const changed = () => this.viewportMobile.set(this.viewport?.matches ?? false);
    this.viewport?.addEventListener('change', changed);
    inject(DestroyRef).onDestroy(() => this.viewport?.removeEventListener('change', changed));
    effect(() => {
      const target = this.pendingPeriodFocus();
      const button = this.periodButtons().find(item => Number(item.nativeElement.dataset['period']) === target);
      if (target !== null && button) {
        button.nativeElement.focus();
        this.pendingPeriodFocus.set(null);
      }
    });
    effect(() => {
      const anchor = this.anchorMonth().withDayOfMonth(1);
      const annual = this.annualYear();
      const month = annual === null ? anchor : anchor.withYear(annual);
      const mode = this.viewMode();
      const position = this.anchorPosition();
      const min = this.effectiveMinDate();
      const max = this.maxDate();
      untracked(() => {
        const pane = mode === 'double' && this.monthCount() === 2 && position === 'right' ? 1 : 0;
        const first = DatepickerNavigation.firstMonth(month, pane, this.monthCount(), min, max);
        if (!first.equals(this.firstMonth())) {
          this.firstMonth.set(first);
          this.activeDate.set(first);
          this.activePane.set(0);
          this.focusDate.set(null);
        }
        this.periodFocus.set(this.activeView() === 'month' ? this.activeDate().monthValue() : this.activeDate().year());
      });
    });
    effect(() => {
      const requested = this.startView();
      const annual = this.annualYear();
      untracked(() => this.showView(annual !== null && requested === 'year' ? 'month' : requested, 0, false));
    });
    effect(() => {
      const count = this.monthCount();
      untracked(() => {
        const pane = count === 1 ? 0 : this.activePane();
        const month = count === 1 && this.activePane() === 1 ? this.secondMonth() : this.firstMonth();
        const first = DatepickerNavigation.firstMonth(month, 0, count, this.effectiveMinDate(), this.maxDate());
        if (!first.equals(this.firstMonth())) {
          this.firstMonth.set(first);
        }
        this.activePane.set(pane);
      });
    });
    effect(() => {
      const start = this.firstMonth();
      const last = this.monthCount() === 2 ? this.secondMonth() : start;
      const end = last.withDayOfMonth(last.lengthOfMonth());
      const key = `${start}/${end}`;
      if (key !== this.lastVisibleRange) {
        this.lastVisibleRange = key;
        untracked(() => this.visibleRangeChange.emit({start, end}));
      }
    });
    effect(() => {
      this.selected();
      this.selectionMode();
      this.fixedStartDate();
      this.minDate();
      this.maxDate();
      this.dateFilter();
      this.anchorDate.set(null);
      this.previewDate.set(null);
    });
  }

  private canNavigate(amount: number): boolean {
    const min = this.effectiveMinDate();
    const max = this.maxDate();
    if (min && max && min.isAfter(max)) {
      return false;
    }
    if (this.activeView() === 'day') {
      if (amount < 0 ? this.previousDisabled() : this.nextDisabled()) {
        return false;
      }
      if (this.annualYear() !== null) {
        return true;
      }
      const edge = amount < 0 || this.monthCount() === 1 ? this.firstMonth() : this.secondMonth();
      const bound = amount < 0 ? this.effectiveMinDate() : this.maxDate();
      return !bound || (amount < 0 ? edge.isAfter(bound.withDayOfMonth(1)) : edge.isBefore(bound.withDayOfMonth(1)));
    }
    if (this.annualYear() !== null) {
      return false;
    }
    const year = this.activeView() === 'year' ? this.yearStart() + amount * 12 : this.activeDate().year() + amount;
    const start = LocalDate.of(year, 1, 1);
    const end = start.plusYears(this.activeView() === 'year' ? 12 : 1).minusDays(1);
    return DatepickerNavigation.intersects(start, end, this.effectiveMinDate(), this.maxDate());
  }

  protected changeMonth(amount: number): void {
    if (!this.canNavigate(amount)) {
      return;
    }
    if (this.activeView() !== 'day') {
      const next = this.activeDate().plusYears(amount * (this.activeView() === 'year' ? 12 : 1));
      this.activeDate.set(this.boundedNavigationDate(next));
      this.periodFocus.set(this.activeView() === 'year' ? this.activeDate().year() : this.activeDate().monthValue());
      return;
    }
    const pane = amount > 0 ? this.monthCount() - 1 : 0;
    const calendar = this.calendars()[pane];
    const date = calendar?.navigationDate ?? this.visibleMonths()[pane];
    const next = this.inNavigationYear(date.plusMonths(amount));
    const first = this.navigationFirstMonth(next, pane);
    // Сохраняем день фокуса, если границы не переставили выбранную сторону.
    if (next.withDayOfMonth(1).equals(first.plusMonths(pane))) {
      calendar?.showDate(next);
    }
    this.focusDate.set(null);
    this.firstMonth.set(first);
  }

  protected showView(view: DatepickerStartView, pane = 0, focus = true): void {
    if (view === 'year' && this.annualYear() !== null) {
      return;
    }
    if (focus && view === this.activeView() && pane === this.activePane() && this.periodsOpenedFromDays) {
      // Возвращаем исходные дни, а не дату, просмотренную в навигации периодов.
      this.activeView.set('day');
      this.pendingPeriodFocus.set(null);
      this.focusDate.set(this.calendars()[pane]?.navigationDate ?? this.visibleMonths()[pane]);
      this.periodsOpenedFromDays = false;
      return;
    }
    if (!focus) {
      this.periodsOpenedFromDays = false;
    } else if (this.activeView() === 'day' && view !== 'day') {
      this.periodsOpenedFromDays = true;
    }
    const date = this.activeView() === 'day'
      ? this.calendars()[pane]?.navigationDate ?? this.visibleMonths()[pane]
      : this.headerMonths()[pane];
    this.activePane.set(pane);
    this.activeDate.set(view === 'day' ? date : this.boundedNavigationDate(date));
    this.activeView.set(view);
    this.focusDate.set(null);
    const value = view === 'month' ? this.activeDate().monthValue() : this.activeDate().year();
    this.periodFocus.set(value);
    this.pendingPeriodFocus.set(focus && view !== 'day' ? value : null);
  }

  protected selectPeriod(cell: CalendarPeriodCell): void {
    if (cell.disabled) {
      return;
    }
    const date = this.activeDate().withDayOfMonth(1);
    if (this.activeView() === 'year') {
      this.activeDate.set(date.withYear(cell.value));
      this.activeView.set('month');
      this.periodFocus.set(date.monthValue());
      this.pendingPeriodFocus.set(date.monthValue());
    } else {
      const month = date.withMonth(cell.value);
      const first = this.navigationFirstMonth(month, this.activePane());
      this.firstMonth.set(first);
      this.activeDate.set(month);
      this.activePane.set(month.equals(first) ? 0 : this.monthCount() - 1);
      this.activeView.set('day');
      this.focusDate.set(month);
    }
  }

  protected onPeriodKeydown(event: KeyboardEvent, value: number): void {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    const months = this.activeView() === 'month';
    const first = months ? 1 : this.yearStart();
    const columns = this.periodColumns();
    const offsets: Record<string, number> = {
      ArrowLeft: -1, ArrowRight: 1, ArrowUp: -columns, ArrowDown: columns,
      Home: -((value - first) % columns), End: columns - 1 - ((value - first) % columns),
      PageUp: -12, PageDown: 12,
    };
    const offset = offsets[event.key];
    if (offset === undefined) {
      return;
    }
    event.preventDefault();
    let date = months
      ? this.inNavigationYear(LocalDate.of(this.activeDate().year(), value, 1).plusMonths(offset))
      : LocalDate.of(value + offset, this.activeDate().monthValue(), 1);
    const samePage = months ? date.year() === this.activeDate().year()
      : date.year() >= this.yearStart() && date.year() < this.yearStart() + 12;
    const targetYear = months ? date.year()
      : this.yearStart() + Math.floor((date.year() - this.yearStart()) / 12) * 12;
    const start = LocalDate.of(targetYear, 1, 1);
    // Недоступные ячейки внутри страницы доступны фокусу, но не выбору.
    if (!samePage && !DatepickerNavigation.intersects(
      start, start.plusYears(months ? 1 : 12).minusDays(1), this.effectiveMinDate(), this.maxDate(),
    )) {
      return;
    }
    if (!samePage && !months) {
      // На частично доступной странице фокус попадает в допустимый год, а не теряется за границей.
      date = this.boundedNavigationDate(date);
    }
    this.activeDate.set(date);
    const next = months ? date.monthValue() : date.year();
    this.periodFocus.set(next);
    this.pendingPeriodFocus.set(next);
  }

  private boundedNavigationDate(date: LocalDate): LocalDate {
    if (this.annualYear() !== null) {
      return date;
    }
    const min = this.effectiveMinDate();
    const max = this.maxDate();
    return min && date.isBefore(min) ? min : max && date.isAfter(max) ? max : date;
  }

  private navigationFirstMonth(date: LocalDate, pane: number): LocalDate {
    return DatepickerNavigation.firstMonth(
      date, pane, this.monthCount(), this.effectiveMinDate(), this.maxDate(),
    );
  }

  protected monthLabel(date: LocalDate): string {
    const month = this.locale.months[date.monthValue() - 1];
    return this.annualYear() === null ? month + ' ' + date.year() : month;
  }

  private inNavigationYear(date: LocalDate): LocalDate {
    return this.annualYear() === null ? date : date.withYear(this.annualYear());
  }

  protected select(date: LocalDate): void {
    if (!DatepickerDateRules.isSelectable(date, this.effectiveMinDate(), this.maxDate(), this.dateFilter())) {
      return;
    }
    if (this.selectionMode() === 'single') {
      this.selected.set(DateRangeSelection.between(date, date));
      return;
    }
    const fixed = this.fixedStartDate();
    if (fixed) {
      if (!DatepickerDateRules.isSelectable(fixed, this.minDate(), this.maxDate(), this.dateFilter())) {
        return;
      }
      this.selected.set({start: fixed, end: date});
      return;
    }
    const first = this.anchorDate();
    if (first) {
      this.anchorDate.set(null);
      this.previewDate.set(null);
      this.selected.set(DateRangeSelection.between(first, date));
    } else {
      this.anchorDate.set(date);
      this.previewDate.set(date);
      this.selectionStarted.emit(date);
    }
  }

  protected preview(date: LocalDate | null): void {
    const allowed = date && DatepickerDateRules.isSelectable(date, this.minDate(), this.maxDate(), this.dateFilter());
    this.previewDate.set(allowed ? date : null);
  }

  protected moveMonth(month: LocalDate, position: AnchorMonthPosition): void {
    this.focusDate.set(null);
    const first = position === 'right' ? month.minusMonths(1) : month;
    this.firstMonth.set(first);
    this.activeDate.set(first);
  }
}
